/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEqual, get } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { config, columns, defaultSorts, trackerFilters } from './config';
import Iframe from '../../components/Iframe';
import PageHeader from '../../components/PageHeader';
import { FilterTypes } from '../../components/Filters';
import TrackerFilters from './TrackerFilters';
import Disclaimer from '../../components/Disclaimer';
import TitleBanner from '../../components/TitleBanner';
import { extractFilterValues } from '../../utils/filters';
import lookerApi from '../../services/looker-api';
import TabsButtonGroup from '../../components/TabsButtonGroup';
import SettingsModal from './SettingsModal';
import Table from '../../components/Table';
import { LineChart } from '../../components/Charts/Linechart';
import InlineQuery from '../../components/InlineQuery';
import ModuleExport from '../../components/ModuleExport';

const formatChartDataTotal = d => {
  const final = [];
  d.forEach(dataPoint => {
    const obj = final.filter(f => f.id === dataPoint.title)[0];
    if (obj) {
      obj.data.push({
        x: dataPoint.releaseDate,
        y: dataPoint.followers,
        yLabel: 'Total Followers',
        newFollowers: dataPoint.totalFollowers,
        title: dataPoint.title
      });
    } else {
      final.push({
        id: dataPoint.title,
        name: dataPoint.title,
        data: [
          {
            x: dataPoint.releaseDate,
            y: dataPoint.followers,
            yLabel: 'Total Followers',
            newFollowers: dataPoint.totalFollowers,
            title: dataPoint.title
          }
        ]
      });
    }
  });

  return final;
};

const formatChartDataNew = d => {
  const final = [];
  d.forEach(dataPoint => {
    const obj = final.filter(f => f.id === dataPoint.title)[0];
    if (obj) {
      obj.data.push({
        x: dataPoint.releaseDate,
        y: dataPoint.totalFollowers,
        yLabel: 'New Followers'
      });
    } else {
      final.push({
        id: dataPoint.title,
        name: dataPoint.title,
        data: [
          {
            x: dataPoint.releaseDate,
            y: dataPoint.totalFollowers,
            yLabel: 'New Followers'
          }
        ]
      });
    }
  });

  return final;
};

const chartQuery = {
  id: 'anticipationTrackerByDay',
  meta: {
    fields: [
      'titleId',
      'age',
      'country',
      'releaseDate',
      'panel',
      'network',
      'gender',
      'genre'
    ]
  },
  sortOptions: ['']
};

const getDefaultDates = () => {
  const storageDates = localStorage.getItem('defaultTrackerDates');
  if (storageDates) {
    const [start, end] = storageDates.split(',');
    return [Number(start), Number(end)];
  }

  return [-40, 14];
};

const AnticipationTracker = ({
  blocked,
  filters,
  queries,
  user,
  filtersActions: { getFiltersForPageAsync, updateFilters, clearFilters },
  queryActions: { runFormattedInlineQueriesForPage },
  segmentActions: { openAudienceSegmentWindow },
  allowedCountries
}) => {
  const [selectedFilters, setSelectedFilters] = useState(
    extractFilterValues(filters.fields)
  );
  const [showTable, setShowTable] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [loadingChartData, setLoadingChartData] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [chartDates, setChartDates] = useState(getDefaultDates());

  const allowedMovies = user.allowedPages.includes('/movies');
  const allowedShows = user.allowedPages.includes('/shows');

  const updateQueries = () => {
    if (showTable) {
      const filterOverrides = {};
      runFormattedInlineQueriesForPage(
        config.ANTICIPATION_TRACKER_REPORT_FILTERS,
        {
          ...filters.fields,
          ...filterOverrides
        }
      );
    }
  };

  const formatChartFilers = array => {
    const data = get(queries, ['anticipationTracker', 'data'], false);
    const days = [{ value: `[${array[0]}, ${array[1]}]` }];
    const showIds = data.slice(0, 10).map(t => ({ value: t.titleId }));

    return {
      ...filters.fields,
      releaseDate: {
        id: 'releaseDate',
        label: 'Days from Release',
        value: days
      },
      titleId: { id: 'titleId', label: 'shows', value: showIds }
    };
  };

  const fetchChartData = async (array = chartDates) => {
    setLoadingChartData(true);
    const data = get(queries, ['anticipationTracker', 'data'], false);
    const dataLoading = get(queries, ['anticipationTracker', 'loading'].false);
    if (data && data.length && !dataLoading) {
      const result = await lookerApi.runInlineQueryStream(
        chartQuery.id,
        chartQuery.meta,
        formatChartFilers(array),
        'json',
        'follower_growth_table_anticipation_tracker.days_from_release'
      );

      if (result && result.data && !get(data, [0, 'errors'], false)) {
        setChartData(result.data);
      }
      setLoadingChartData(false);
    }
  };

  const onApplySettings = array => {
    setChartDates(array);
    fetchChartData(array);
  };

  const onClickSettings = () => setSettingsModalOpen(true);

  const onCloseSettings = () => setSettingsModalOpen(false);

  const downloadReportFunc = () => (api, type, title) => {
    return api(
      {
        view: chartQuery.id,
        filters: formatChartFilers(chartDates),
        queryMeta: chartQuery.meta
      },
      type,
      title
    );
  };

  useEffect(() => {
    if (showTable) {
      fetchChartData();
    }
  }, [queries.anticipationTracker]);

  useEffect(() => {
    if (!blocked) {
      if (filters.fields.country) {
        const f = filters.fields.country;
        if (f.value) {
          let i;
          for (i = 0; i < f.value.length; i += 1) {
            f.value[i].base = f.value[i].value;
            f.value[i].state = 'included';
          }
        }
      }

      const values = extractFilterValues(filters.fields);
      const hasNewValues = !isEqual(values, selectedFilters);

      const contentFields = trackerFilters.content;

      let hasContentValues = false;
      values.forEach(v => {
        const [k] = v.split(':');
        if (contentFields.filter(cf => cf.id === k).length > 0) {
          hasContentValues = true;
        }
      });
      setShowTable(hasContentValues);

      if (hasNewValues) {
        setSelectedFilters(values);
        updateQueries();
      }

      if (selectedFilters.length === 0 && !Object.keys(filters.fields).length) {
        // handle "clear all" from TabbedFilter component
        getFiltersForPageAsync(config.ANTICIPATION_TRACKER_REPORT_PAGE_ID);
      }
    }
  }, [filters.fields]);

  useEffect(() => {
    updateQueries();
  }, [showTable]);

  useEffect(() => {
    getFiltersForPageAsync(config.ANTICIPATION_TRACKER_REPORT_PAGE_ID);
    document.title = 'Anticipation | Tracker | CVM Insights';
  }, []);

  return (
    <>
      <TitleBanner>
        <PageHeader
          title="Anticipation Tracker"
          tooltip="The Anticipation Tracker monitors anticipation and intent to view a title. The visualizations present a normalized view of follower growth relative to release day."
        />
      </TitleBanner>
      {blocked && (
        <Iframe
          blocked
          blockedMessage="Contact your Client Success Representative to learn more about this report"
          url=""
          height="100%"
        />
      )}
      {!blocked && Object.keys(filters.fields).length > 0 && (
        <TrackerFilters
          filterFields={filters.fields}
          onApply={updateFilters}
          onReset={clearFilters}
          showTable={showTable}
          openSegment={openAudienceSegmentWindow}
          allowedShows={allowedShows}
          allowedMovies={allowedMovies}
          allowedCountries={allowedCountries}
        />
      )}

      {!blocked && (
        <>
          <section className="tvt-main__content">
            <Row
              className={`tvt-anticipation-tracker__main-content ${
                !showTable ? 'hide-content' : ''
              }`}
            >
              <Col
                className="tvt-anticipation-tracker__main-content-left-col"
                md={12}
              >
                {showTable && (
                  <>
                    <div className="tvt-anticipation-tracker__charts-wrapper">
                      <button
                        className="tvt-anticipation-trackker__options-button"
                        onClick={onClickSettings}
                      >
                        <FontAwesomeIcon
                          className="tvt-anticipation-trackker__options-button-icon"
                          icon="cogs"
                          size="1x"
                        />
                      </button>
                      <SettingsModal
                        open={settingsModalOpen}
                        onClose={onCloseSettings}
                        onApplySettings={onApplySettings}
                        appliedStart={chartDates[0]}
                        appliedEnd={chartDates[1]}
                      />
                      <ModuleExport
                        fetchData
                        showTitle="Anticipation Tracker "
                        tableDataDownload={downloadReportFunc(
                          chartQuery.id,
                          formatChartFilers(chartDates),
                          chartQuery.meta
                        )}
                        csv
                        xls
                        png
                      />
                      <TabsButtonGroup id="tvt-show-engagement__characters">
                        <div icon="TOTAL">
                          <div className="tvt-show-report__anticipation-line-chart-wrapper">
                            <LineChart
                              axisBottomLegend="Days from Release"
                              data={formatChartDataTotal(chartData)}
                              loading={
                                loadingChartData ||
                                get(
                                  queries,
                                  ['anticipationTracker', 'loading'],
                                  false
                                )
                              }
                              range={chartDates}
                            />
                          </div>
                        </div>
                        <div icon="NET NEW">
                          <div className="tvt-show-report__anticipation-line-chart-wrapper">
                            <LineChart
                              axisBottomLegend="Days from Release"
                              data={formatChartDataNew(chartData)}
                              loading={
                                loadingChartData ||
                                get(
                                  queries,
                                  ['anticipationTracker', 'loading'],
                                  false
                                )
                              }
                              range={chartDates}
                            />
                          </div>
                        </div>
                      </TabsButtonGroup>
                    </div>
                    <div className="tvt-anticipation-tracker__table-wrapper">
                      <InlineQuery
                        useChildForLoading
                        query={queries.anticipationTracker}
                      >
                        {query => (
                          <Table
                            data={query.data}
                            headerFormatter={columns}
                            defaultSorts={defaultSorts}
                            view="anticipationTracker"
                            height={438}
                            width={1158}
                            filters={filters.fields}
                            showRank={false}
                            queryMeta={
                              config.ANTICIPATION_TRACKER_REPORT_FILTERS.queries
                                .anticipationTracker
                            }
                          />
                        )}
                      </InlineQuery>
                    </div>
                  </>
                )}
              </Col>
            </Row>

            {!showTable && (
              <div style={{ margin: '40px 0 20px' }}>
                <Disclaimer>
                  <div className="affinity-report-disclaimer-wrapper">
                    To generate the report, select from the above criteria in
                    the Audience Builder to define your seed audience, or use an
                    existing segment.
                  </div>
                </Disclaimer>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
};

AnticipationTracker.propTypes = {
  blocked: PropTypes.bool.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPageAsync: PropTypes.func.isRequired,
    updateFilters: PropTypes.func.isRequired,
    clearFilters: PropTypes.func.isRequired
  }).isRequired,
  segmentActions: PropTypes.shape({
    openAudienceSegmentWindow: PropTypes.func.isRequired
  }).isRequired,
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  queries: PropTypes.shape({}).isRequired
};

export default AnticipationTracker;
